import { useState, type MouseEvent } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { LogOut } from 'lucide-react'
import { useAuthStore } from '~/stores/auth-store'
import { useUiStore } from '~/stores/ui-store'
import { env } from '~/env'
import { Button } from '~/components/ui/button'
import { ProfileModal } from '~/components/layout/profile-modal'
import { cn } from '~/lib/utils'

// hidden 항목은 기본 노출 — 푸터 저작권 7연속 클릭(이스터에그)으로 숨김/노출 토글 가능.
const NAV_ITEMS: { to: string; label: string; hidden?: boolean }[] = [
    { to: '/certification', label: '모의 평가' },
    { to: '/consultation', label: '상담 챗봇' },
    { to: '/explore', label: '찾아보기' },
    { to: '/maintenance', label: '변경 검토', hidden: true },
    { to: '/monitoring', label: '모니터링', hidden: true },
]

/** 상단 헤더. 브랜드 락업 + 서비스 GNB + 로그인 상태/로그아웃. */
export function AppHeader() {
    const user = useAuthStore((s) => s.user)
    const clear = useAuthStore((s) => s.clear)
    const hiddenServicesRevealed = useUiStore((s) => s.hiddenServicesRevealed)
    const [profileOpen, setProfileOpen] = useState(false)
    const navigate = useNavigate()

    // 유지하기는 화면 내부 단계(state)를 가지므로, 같은 경로로의 NavLink 클릭만으로는
    // 홈 단계로 돌아가지 못한다(#15). 매 클릭마다 새 신호(state)를 실어 보내 페이지가
    // 1단계(대상 평가)로 복귀하게 한다.
    const handleNavClick = (
        event: MouseEvent<HTMLAnchorElement>,
        to: string
    ) => {
        if (to === '/maintenance') {
            event.preventDefault()
            navigate('/maintenance', { state: { maintenanceHome: Date.now() } })
        }
    }

    // 로그아웃은 서버사이드: 쿠키·세션을 비우고 knoka end_session까지 처리하도록
    // 백엔드 /auth/logout으로 전체 페이지를 이동한다(스토어는 선제적으로 비운다).
    const handleLogout = () => {
        clear()
        const base = env.API_BASE_URL.replace(/\/$/, '')
        window.location.href = `${base}/auth/logout`
    }

    return (
        <header className='bg-navy text-navy-foreground sticky top-0 z-50 border-b border-white/15 shadow-sm'>
            <div className='mx-auto flex h-16 w-full max-w-[1680px] items-center gap-6 px-4 lg:px-8 2xl:px-10'>
                <NavLink
                    to='/'
                    className='flex shrink-0 items-center gap-2 text-base font-bold tracking-tight'
                    aria-label='HACCP AI 홈'>
                    <img
                        src='/brand/logo-white.svg'
                        alt=''
                        className='size-8'
                        aria-hidden='true'
                    />
                    <span>HACCP AI</span>
                </NavLink>

                <nav
                    aria-label='주요 메뉴'
                    className='hidden h-full items-stretch gap-1 md:flex'>
                    {NAV_ITEMS.filter(
                        (item) => !item.hidden || hiddenServicesRevealed
                    ).map((item) => (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            onClick={(e) => handleNavClick(e, item.to)}
                            className={({ isActive }) =>
                                cn(
                                    'inline-flex items-center border-b-[3px] px-3 text-sm font-bold transition-colors',
                                    isActive
                                        ? 'border-white text-white'
                                        : 'border-transparent text-white/76 hover:text-white'
                                )
                            }
                            end={item.to === '/certification'}>
                            {item.label}
                        </NavLink>
                    ))}
                </nav>

                <div className='ml-auto flex items-center gap-3'>
                    {user ? (
                        <button
                            type='button'
                            onClick={() => setProfileOpen(true)}
                            aria-label='내 정보'
                            title='내 정보'
                            className='hidden items-center gap-2 rounded-sm px-1.5 py-1 transition-colors hover:bg-white/10 sm:flex'>
                            <span className='text-sm text-white/78'>
                                {user.name || user.email}
                            </span>
                            <span
                                className='grid size-7 place-items-center rounded-sm border border-white/22 bg-white/12 text-xs font-bold'
                                aria-hidden='true'>
                                {(user.name || user.email || '사').slice(0, 1)}
                            </span>
                        </button>
                    ) : null}
                    <Button
                        variant='ghost'
                        size='sm'
                        onClick={handleLogout}
                        className='text-white hover:bg-white/10 hover:text-white'>
                        <LogOut />
                        로그아웃
                    </Button>
                </div>
            </div>

            <ProfileModal
                open={profileOpen}
                onClose={() => setProfileOpen(false)}
            />
        </header>
    )
}
